'use client';

import { useQuery } from '@tanstack/react-query';
import ScheduleSlider from './ScheduleSlider';
import ScheduleCalendarGrid from './ScheduleCalendarGrid';
import { useSearchParams } from 'next/navigation';
import { getScheduleSlots } from '@/app/actions/getScheduleSlots';
import {
  Fragment,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  getCalendarSlot,
  getCurrentWeekDay,
  getDayRange,
  isSameDay
} from '@/app/utility/date';
import { useInViewport } from 'react-in-viewport';
import LockBodyScroll from 'app/components/common/LockBodyScroll';
import {
  TrainingTypeEnum,
  TrainingType,
  IScheduleSlot
} from '@/app/interfaces/schedule';

function getSliderInitilaIndex() {
  return (6 + getCurrentWeekDay()) % 7;
}

function ScheduleTableInner({ branch }: { branch?: string }) {
  const searchParams = useSearchParams();
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const blockRef = useRef<HTMLDivElement | null>(null);
  const { inViewport } = useInViewport(blockRef, {
    rootMargin: '0% 0% -40% 0%',
    threshold: [0, 1]
  });

  const trainingType = useMemo(() => {
    return searchParams.get('training_type') as TrainingType;
  }, [searchParams]);

  const { data: slots, isFetching } = useQuery({
    queryFn: async () =>
      await getScheduleSlots({
        training_type: trainingType,
        branch: searchParams.get('branch') || branch || '',
        specialist: searchParams.getAll('specialist')
      }),
    queryKey: ['slots', searchParams.toString()],
    enabled:
      !!trainingType && Object.values(TrainingTypeEnum).includes(trainingType),
    placeholderData: prev => prev
  });

  const dateRange = useMemo(() => {
    if (!slots?.length) return [];
    const firstDate = slots[0].start_date;
    const lastDate = slots[slots.length - 1].start_date;
    return getDayRange(firstDate, lastDate, true);
  }, [slots]);

  const calendarSlots = useMemo(() => {
    return dateRange.map(date => getCalendarSlot(date));
  }, [dateRange]);

  const [calendarActiveRangeIndexStart, setCalendarActiveRangeIndexStart] =
    useState<number | null>(null);
  const [calendarActiveRangeLength, setCalendarActiveRangeLength] = useState<
    number | null
  >(null);

  const scheduleSlotColumns = useMemo(() => {
    if (!slots?.length || !dateRange.length) return [];
    if (
      typeof calendarActiveRangeIndexStart !== 'number' ||
      typeof calendarActiveRangeLength !== 'number'
    )
      return [];

    return dateRange
      .map(date => {
        return slots.filter(slot => {
          return isSameDay(slot.start_date, date);
        });
      })
      .slice(
        calendarActiveRangeIndexStart,
        calendarActiveRangeIndexStart + calendarActiveRangeLength
      );
  }, [
    dateRange,
    slots,
    calendarActiveRangeIndexStart,
    calendarActiveRangeLength
  ]);

  const scheduleSlotRows = useMemo(() => {
    if (!scheduleSlotColumns.length) return [];

    const maxSlots = Math.max(
      ...scheduleSlotColumns.map(column => column.length)
    );

    const rows: (IScheduleSlot | null)[][] = [];
    for (let rowIndex = 0; rowIndex < maxSlots; rowIndex++) {
      const row: (IScheduleSlot | null)[] = [];
      for (
        let colIndex = 0;
        colIndex < scheduleSlotColumns.length;
        colIndex++
      ) {
        const column = scheduleSlotColumns[colIndex];
        row.push(column[rowIndex] || null);
      }
      rows.push(row);
    }

    return rows;
  }, [scheduleSlotColumns]);

  useEffect(() => {
    if (!isInitialLoading) return;
    // Сбрасываем начальную загрузку по факту завершения первого запроса,
    // даже если расписание пустое (slots === []). Иначе при пустом/ошибочном
    // первом ответе спиннер «Загрузка...» висит вечно, а «Ничего не найдено»
    // недостижимо.
    if (!isFetching && slots !== undefined) {
      setIsInitialLoading(false);
    }
  }, [isFetching, slots, isInitialLoading]);

  if (isInitialLoading)
    return (
      <div className="w-full wrapper-main wrapper-max flex justify-center items-center py-10">
        Загрузка...
      </div>
    );

  if (!isFetching && !slots?.length)
    return (
      <div className="w-full wrapper-main wrapper-max flex justify-center items-center py-10">
        Ничего не найдено
      </div>
    );

  return (
    <Fragment>
      <div ref={blockRef} className="flex flex-col gap-8">
        {!!calendarSlots?.length && (
          <div className="w-full wrapper-main wrapper-max sticky top-25 z-1">
            <ScheduleSlider
              items={calendarSlots}
              onChange={(activeRangeIndexStart, calendarActiveRangeLength) => {
                setCalendarActiveRangeIndexStart(activeRangeIndexStart);
                setCalendarActiveRangeLength(calendarActiveRangeLength);
              }}
              initialIndex={getSliderInitilaIndex()}
            />
          </div>
        )}

        <div className="w-full wrapper-main wrapper-max">
          <div className="xl:px-12">
            <ScheduleCalendarGrid rows={scheduleSlotRows} />
          </div>
        </div>
      </div>
      {isFetching && inViewport && (
        <Fragment>
          <LockBodyScroll />
          <div className="fixed w-full h-full top-0 left-0 wrapper-main flex justify-center items-center py-10 z-50">
            <div className="absolute inset-0 bg-gray opacity-60"></div>
            <span className="spinner w-14 h-14 border-2 border-brown"></span>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
}

export default function ScheduleTable(props: { branch?: string }) {
  return (
    <Suspense
      fallback={
        <div className="w-full wrapper-main wrapper-max flex justify-center items-center py-10">
          Загрузка...
        </div>
      }
    >
      <ScheduleTableInner {...props} />
    </Suspense>
  );
}
